import React, { useEffect, useState } from 'react';
import { stripePromise } from '../lib/stripe';
import Button from '../components/ui/Button';
import { giftVoucherService } from '../services/giftVoucherService';
import SEOHead from '../components/SEOHead';

const PaymentStatus: React.FC = () => {
  const [status, setStatus] = useState<'success' | 'processing' | 'failed' | 'error' | ''>('');
  const [message, setMessage] = useState<string>('');
  const [context, setContext] = useState<'booking' | 'voucher' | undefined>(undefined);
  
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const clientSecret = params.get('payment_intent_client_secret');
    if (!clientSecret) return;

    (async () => {
      try {
        const stripe = await stripePromise;
        if (!stripe) return;
        const { paymentIntent, error } = await stripe.retrievePaymentIntent(clientSecret);
        if (error || !paymentIntent) {
          setStatus('error');
          setMessage(error?.message || "Impossible de récupérer le statut du paiement.");
          return;
        }
        const meta: any = paymentIntent.metadata || {};
        setContext(meta.type as any);
        
        // If voucher payment succeeded/processing, mark it as paid in backend (post-redirect)
        if (
          (paymentIntent.status === 'succeeded' ||
            paymentIntent.status === 'processing' ||
            paymentIntent.status === 'requires_capture') &&
          meta.type === 'voucher'
        ) {
          const voucherId = meta.voucher_id ? parseInt(meta.voucher_id, 10) : undefined;
          if (voucherId && paymentIntent.id) {
            try {
              await giftVoucherService.markPaid(voucherId, paymentIntent.id);
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error('Erreur de confirmation paiement bon cadeau (PaymentStatus):', err);
            }
          }
        }

                
        // --- Fallback UI (if not a voucher or if redirect fails) ---
        switch (paymentIntent.status) {
          case 'succeeded':
          case 'requires_capture':
            setStatus('success');
            setMessage('Paiement confirmé. Merci !');
            break;
          case 'processing':
            setStatus('processing');
            setMessage('Votre paiement est en cours de traitement. Redirection en cours…');
            break;
          case 'requires_payment_method':
          default:
            setStatus('failed');
            setMessage(paymentIntent.last_payment_error?.message || 'Le paiement a été annulé ou a échoué.');
            if (context === 'voucher') {
              window.location.replace('/bons-cadeaux?payment_failed=true');
              return;
            }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Erreur PaymentStatus:', e);
        setStatus('error');
        setMessage('Une erreur est survenue lors de la vérification du paiement.');
      }
    })();
  }, []);

  // Auto-redirect based on status
  
  const goBackPath = context === 'voucher' ? '/bons-cadeaux' : '/reservation';

  return (
    <div className="pt-24 pb-16 bg-neutral-offwhite">
      <SEOHead title="Statut du paiement" path="/paiement" noindex />
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow p-8 text-center">
          <h1 className={`font-playfair text-3xl font-bold mb-2 ${status === 'success' ? 'text-green-700' : status === 'failed' || status === 'error' ? 'text-red-700' : 'text-neutral-ink'}`}>Statut du paiement</h1>
          {status && (
            <div className="mb-4">
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                status === 'success' ? 'bg-green-100 text-green-800' :
                status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {status === 'success' ? 'Réussi' : status === 'processing' ? 'En cours' : 'Échoué'}
              </span>
            </div>
          )}
          <p className="font-nunito text-neutral-body mb-6">{message}</p>

          <div className="flex gap-4 justify-center">
            <Button to={goBackPath} variant="primary">Retour</Button>
            <Button to="/" variant="secondary">Accueil</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentStatus;
